import { environmentHttpClient } from "client/Api/httpClient";
import { EmailListItemModel } from "client/Api/Email/email";

enum ContentKind {
	Unread = 0,
	ReadAndUnread = 1
}

enum MailboxIdentifierKind {
	Default = 0,
	Specific = 1
}

interface DefaultMailboxIdentifier {
	kind: MailboxIdentifierKind.Default;
}

interface SpecificMailboxIdentifier {
	kind: MailboxIdentifierKind.Specific;
	smtpAddress: string;
}

enum FolderIdentifierKind {
	WellKnown = 0,
	Specific = 1
}

enum WellKnownFolder {
	Inbox = 0
}

interface WellKnownFolderIdentifier {
	kind: FolderIdentifierKind.WellKnown;
	folder: WellKnownFolder;
}

interface SpecificFolderIdentifier {
	kind: FolderIdentifierKind.Specific;
	id: string;
	name: string;
}

type MailboxIdentifier = DefaultMailboxIdentifier | SpecificMailboxIdentifier;
type FolderIdentifier = WellKnownFolderIdentifier | SpecificFolderIdentifier;

interface MailboxIdentifierModel {
	default?: DefaultMailboxIdentifierModel;
	specific?: SpecificMailboxIdentifierModel;
}

interface DefaultMailboxIdentifierModel { }

interface SpecificMailboxIdentifierModel {
	smtpAddress: string;
}

interface FolderIdentifierModel {
	wellKnown?: WellKnownFolderIdentifierModel;
	specific?: SpecificFolderIdentifierModel;
}

interface WellKnownFolderIdentifierModel {
	folder: WellKnownFolder;
}

interface SpecificFolderIdentifierModel {
	id: string;
	name: string;
}

type Interests = {
	unreadCount?: boolean;
	unreadItems?: boolean;
	items?: boolean;
};

interface LiveInformationRequestModel {
	mailbox: MailboxIdentifierModel;
	folder: FolderIdentifierModel;
	interestedIn: Interests;
}

interface LiveInformationResponseModel {
	mailbox: MailboxIdentifier;
	folder: FolderIdentifier;
	interestedIn: Interests;
	data: LiveInformationResponseModelData;
}

interface LiveInformationResponseModelData {
	unreadCount?: UnreadCountResult;
	unreadItems?: ItemsResult;
	items?: ItemsResult;
}

type UnreadCountResult =
	{ failed: true; }
	|
	{ failed: false; amount: number; };

enum ItemsResultStatus {
	GeneralFailure = 0,
	Success = 1,
	NoAccess = 2
}

type ItemsResult =
	{ status: ItemsResultStatus.GeneralFailure | ItemsResultStatus.NoAccess; }
	|
	{ status: ItemsResultStatus.Success; items: LazyLoadPageResult<EmailListItemModel>; };

enum ExchangeFolderIdResultStatus {
	Success = 0,
	RootFolderLoaded = 1,
	CompleteFailure = 2
}

export type LoadMoreRequestModel = {
	mailbox: MailboxIdentifierModel;
	folder: FolderIdentifierModel;
	pageNumber: number;
	shouldRestrictToUnreadItems: boolean;
};

function getLiveInformation(models: LiveInformationRequestModel[]): Promise<LiveInformationResponseModel[]> {
	return environmentHttpClient.post<LiveInformationResponseModel[]>(
		"Workspace/LiveInformation/Email",
		models
	);
}

export function loadMore(
	model: LoadMoreRequestModel,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<EmailListItemModel>> {
	return environmentHttpClient.post<LazyLoadPageResult<EmailListItemModel>>(
		"Workspace/LiveInformation/Email/LoadMore",
		model,
		{ signal }
	);
}

function convertMailboxIdentifierToModel(identifier: MailboxIdentifier): MailboxIdentifierModel {
	switch (identifier.kind) {
		case MailboxIdentifierKind.Default:
			return {
				// We need to send garbage else MVC model binding is nice enough to make the entire tree of objects
				// into null references
				default: { "_": 0 }
			};
		case MailboxIdentifierKind.Specific:
			return {
				specific: convertSpecificMailboxIdentifierToModel(identifier)
			};
		default:
			return unknownMailboxIdentifier(identifier);
	}
}

function convertSpecificMailboxIdentifierToModel(identifier: SpecificMailboxIdentifier): SpecificMailboxIdentifierModel {
	return {
		smtpAddress: identifier.smtpAddress
	};
}

function unknownMailboxIdentifier(identifier: never): never {
	throw new Error(`Unknown MailboxIdentifier ${JSON.stringify(identifier)}`);
}

function convertFolderIdentifierToModel(identifier: FolderIdentifier): FolderIdentifierModel {
	switch (identifier.kind) {
		case FolderIdentifierKind.WellKnown:
			return {
				wellKnown: {
					folder: identifier.folder
				}
			};
		case FolderIdentifierKind.Specific:
			return {
				specific: {
					id: identifier.id,
					name: identifier.name
				}
			};
		default:
			return unknownFolderIdentifier(identifier);
	}
}

function unknownFolderIdentifier(identifier: never): never {
	throw new Error(`Unknown FolderIdentifier ${JSON.stringify(identifier)}`);
}

export {
	getLiveInformation,
	convertMailboxIdentifierToModel,
	convertSpecificMailboxIdentifierToModel,
	convertFolderIdentifierToModel,
	unknownMailboxIdentifier,
	unknownFolderIdentifier,
	MailboxIdentifierKind,
	type DefaultMailboxIdentifier,
	type SpecificMailboxIdentifier,
	type SpecificMailboxIdentifierModel,
	type MailboxIdentifier,
	type MailboxIdentifierModel,
	FolderIdentifierKind,
	type WellKnownFolderIdentifier,
	type WellKnownFolderIdentifierModel,
	WellKnownFolder,
	type SpecificFolderIdentifier,
	type SpecificFolderIdentifierModel,
	type FolderIdentifier,
	type FolderIdentifierModel,
	type Interests,
	type LiveInformationRequestModel,
	type LiveInformationResponseModel,
	type LiveInformationResponseModelData,
	type UnreadCountResult,
	ItemsResultStatus,
	type ItemsResult,
	ContentKind,
	ExchangeFolderIdResultStatus
};
